import React, { useState, useEffect } from "react";
import Layout from "src/layout";
import Container from "@/components/ui/container";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import InputUi from "@/components/ui/inputui";
import { createSectionItem } from "src/lib/api/sectionItemsApi";
import { listCustomImageSections } from "src/lib/api/customImageSectionsApi";
import { listProductGroups } from "src/lib/api/productGroupsApi";
import { listPresaleSectionGroups } from "src/lib/api/presaleSectionGroupsApi";
import { getAllFeaturedCoupons, createFeaturedCoupon } from "src/lib/api/featuredCouponsApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    RiImageAddLine,
    RiCloseLine,
    RiCoupon3Line,
    RiCheckboxCircleLine,
    RiArrowLeftLine,
    RiImageLine
} from "react-icons/ri";

const BUNNY = {
    storageZone: 'ithyaraa',
    storageRegion: 'sg.storage.bunnycdn.com',
    pullZoneUrl: 'https://ithyaraa.b-cdn.net',
    apiKey: '7017f7c4-638b-48ab-add3858172a8-f520-4b88',
};

const uploadToBunny = async (file) => {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const fileName = `featured-coupon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const uploadUrl = `https://${BUNNY.storageRegion}/${BUNNY.storageZone}/${fileName}`;
    const publicUrl = `${BUNNY.pullZoneUrl}/${fileName}`;

    const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            AccessKey: BUNNY.apiKey,
            'Content-Type': file.type || 'image/jpeg',
        },
        body: file,
    });

    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    return publicUrl;
};

const AddSectionItem = () => {
    const [itemId, setItemId] = useState('');
    const [type, setType] = useState('imagesection');
    const [order, setOrder] = useState(0);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Featured coupon state
    const [popupImageFile, setPopupImageFile] = useState(null);
    const [iconImageFile, setIconImageFile] = useState(null);
    const [popupImagePreview, setPopupImagePreview] = useState(null);
    const [iconImagePreview, setIconImagePreview] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [uploading, setUploading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (type === 'featuredcoupon') {
            if (!popupImageFile || !iconImageFile || !couponCode) {
                toast.error('Popup image, icon image, and coupon code are required');
                return;
            }

            try {
                setLoading(true);
                setUploading(true);

                const [popupUrl, iconUrl] = await Promise.all([
                    uploadToBunny(popupImageFile),
                    uploadToBunny(iconImageFile),
                ]);

                const couponRes = await createFeaturedCoupon({
                    popupImage: popupUrl,
                    iconImage: iconUrl,
                    couponCode: couponCode
                });

                if (!couponRes.success) {
                    toast.error(couponRes.error || 'Failed to create featured coupon');
                    return;
                }

                const sectionRes = await createSectionItem({
                    id: String(couponRes.id),
                    type: 'featuredcoupon',
                    order: Number(order),
                });

                if (sectionRes.success) {
                    toast.success('Featured Coupon added to homepage!');
                    navigate('/section-items/list');
                } else {
                    toast.error(sectionRes.message || 'Failed to create section item');
                }
            } catch (err) {
                console.error(err);
                toast.error(err.message || 'Failed to create');
            } finally {
                setLoading(false);
                setUploading(false);
            }
            return;
        }

        if (!itemId || !type) {
            toast.error('Item ID and Section Type are required');
            return;
        }
        try {
            setLoading(true);
            const res = await createSectionItem({ id: itemId, type, order: Number(order) });
            if (res.success) {
                toast.success('Section item added to homepage layout!');
                navigate('/section-items/list');
            } else {
                toast.error(res.message || 'Failed to create section item');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to create section item');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                if (type === 'imagesection') {
                    const res = await listCustomImageSections({ page: 1, limit: 100 });
                    if (res.success) {
                        setOptions(res.data || []);
                    } else setOptions([]);
                } else if (type === 'productsection' || type === 'product section') {
                    const res = await listProductGroups({ page: 1, limit: 100 });
                    if (res.success) {
                        setOptions(res.data || []);
                    } else setOptions([]);
                } else if (type === 'presalesection' || type === 'presale section') {
                    const res = await listPresaleSectionGroups({ page: 1, limit: 100 });
                    if (res.success) {
                        setOptions(res.data || []);
                    } else setOptions([]);
                } else if (type === 'featuredcoupon') {
                    const res = await getAllFeaturedCoupons();
                    if (res.success) {
                        setOptions(res.data || []);
                    } else setOptions([]);
                } else {
                    setOptions([]);
                }
                setSelectedOption(null);
                setItemId('');
                setSearch('');
                setPopupImageFile(null);
                setIconImageFile(null);
                setPopupImagePreview(null);
                setIconImagePreview(null);
                setCouponCode('');
            } catch (err) {
                console.error(err);
                setOptions([]);
            }
        };
        fetchOptions();
    }, [type]);

    const filteredOptions = options.filter(opt => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        if (type === 'imagesection') {
            return String(opt.title || '').toLowerCase().includes(q) || String(opt.sectionID || '').toLowerCase().includes(q);
        }
        if (type === 'productsection' || type === 'product section') {
            return String(opt.sectionID || '').toLowerCase().includes(q) || String(opt.id || '').toLowerCase().includes(q);
        }
        if (type === 'presalesection' || type === 'presale section') {
            return String(opt.sectionID || '').toLowerCase().includes(q) || String(opt.id || '').toLowerCase().includes(q) || String(opt.title || '').toLowerCase().includes(q);
        }
        if (type === 'featuredcoupon') {
            return String(opt.id || '').includes(q);
        }
        return true;
    });

    const handleFileSelect = (e, setFile, setPreview) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFile(file);
        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const clearFile = (setFile, setPreview) => {
        setFile(null);
        setPreview(null);
    };

    return (
        <Layout active="admin-section-items-add" title="Add Section Item">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <button
                                onClick={() => navigate('/section-items/list')}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-indigo-600 mb-2 transition-colors"
                            >
                                <RiArrowLeftLine className="w-4 h-4" /> Back to Section Items
                            </button>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiImageLine className="text-indigo-600" /> Add Section Item
                            </h1>
                            <p className="text-slate-500 text-sm mt-0.5">
                                Mount custom image sections, product group blocks, or floating coupons to homepage.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/section-items/list')}
                                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={loading || uploading}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm min-w-[130px]"
                            >
                                {uploading ? 'Uploading...' : loading ? 'Saving...' : 'Save Section Item'}
                            </Button>
                        </div>
                    </div>

                    {/* Form Card */}
                    <form onSubmit={handleSubmit} className="max-w-3xl bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-6">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                Section Type *
                            </label>
                            <select
                                value={type}
                                onChange={e => setType(e.target.value)}
                                className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                            >
                                <option value="imagesection">imagesection (Custom Image Banner Section)</option>
                                <option value="productsection">productsection (Product Group Row)</option>
                                <option value="presalesection">presalesection (Presale Booking Block)</option>
                                <option value="featuredcoupon">featuredcoupon (Floating Widget)</option>
                            </select>
                        </div>

                        {/* Featured Coupon Config */}
                        {type === 'featuredcoupon' && (
                            <div className="space-y-6 bg-slate-50 border border-slate-200 rounded-xl p-5">
                                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                                    <RiCoupon3Line className="w-5 h-5 text-amber-500" />
                                    <h3 className="font-semibold text-slate-900 text-sm">Featured Floating Coupon Setup</h3>
                                </div>

                                <InputUi
                                    label="Coupon Code *"
                                    placeholder="e.g. SAVE50"
                                    value={couponCode}
                                    datafunction={e => setCouponCode(e.target.value)}
                                />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                            Popup Image *
                                        </label>
                                        {popupImagePreview ? (
                                            <div className="relative inline-block">
                                                <img src={popupImagePreview} alt="Popup preview" className="w-48 h-auto rounded-lg border border-slate-200 shadow-sm" />
                                                <button
                                                    type="button"
                                                    onClick={() => clearFile(setPopupImageFile, setPopupImagePreview)}
                                                    className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow hover:bg-rose-600"
                                                >
                                                    <RiCloseLine className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50/50 transition-colors">
                                                <RiImageAddLine className="w-8 h-8 text-amber-500 mb-1" />
                                                <span className="text-xs font-semibold text-amber-700">Upload Popup Image</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => handleFileSelect(e, setPopupImageFile, setPopupImagePreview)}
                                                />
                                            </label>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                            Floating Icon Image *
                                        </label>
                                        {iconImagePreview ? (
                                            <div className="relative inline-block">
                                                <img src={iconImagePreview} alt="Icon preview" className="w-20 h-20 rounded-full border border-slate-200 shadow-sm object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => clearFile(setIconImageFile, setIconImagePreview)}
                                                    className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow hover:bg-rose-600"
                                                >
                                                    <RiCloseLine className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-amber-300 rounded-full cursor-pointer hover:bg-amber-50/50 transition-colors">
                                                <RiImageAddLine className="w-6 h-6 text-amber-500" />
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => handleFileSelect(e, setIconImageFile, setIconImagePreview)}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Standard Selector */}
                        {type !== 'featuredcoupon' && (
                            <div className="space-y-3">
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                    Target Section Block *
                                </label>
                                <div className="flex gap-2">
                                    <Input
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        placeholder="Search available blocks by ID or Title..."
                                        className="h-10 text-sm"
                                    />
                                    {search && (
                                        <Button type="button" variant="outline" onClick={() => setSearch('')} className="border-slate-200 text-slate-600">
                                            Clear
                                        </Button>
                                    )}
                                </div>

                                <div className="max-h-48 overflow-auto border border-slate-200 rounded-lg p-2 bg-slate-50/50 divide-y divide-slate-100">
                                    {filteredOptions.length === 0 ? (
                                        <div className="text-xs text-slate-400 p-3 text-center">No matching section blocks found</div>
                                    ) : filteredOptions.map(opt => {
                                        const key = opt.sectionID || opt.id;
                                        const label = `${key} — ${opt.title || opt.name || 'Untitled Block'}`;
                                        const isSelected = selectedOption && String(selectedOption.key) === String(key);
                                        return (
                                            <div
                                                key={key}
                                                className={`p-2.5 rounded-md cursor-pointer text-xs transition-colors ${
                                                    isSelected ? 'bg-indigo-50 border border-indigo-200 text-indigo-900 font-semibold' : 'hover:bg-white text-slate-700'
                                                }`}
                                                onClick={() => {
                                                    setSelectedOption({ key, label, raw: opt });
                                                    setItemId(key);
                                                }}
                                            >
                                                {label}
                                            </div>
                                        );
                                    })}
                                </div>

                                {selectedOption && (
                                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-800 flex items-center justify-between">
                                        <span>Selected Target: <strong>{selectedOption.label}</strong></span>
                                        <RiCheckboxCircleLine className="w-4 h-4 text-emerald-600" />
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <InputUi
                                label="Order Sequence Index"
                                type="number"
                                placeholder="0"
                                value={order}
                                datafunction={e => setOrder(e.target.value)}
                            />
                        </div>
                    </form>
                </Container>
            </div>
        </Layout>
    );
};

export default AddSectionItem;
